import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import PropTypes from "prop-types";

import * as amplitude from "@amplitude/analytics-browser";

import {
  selectIsSkuInCart,
  selectIsBundleInCart,
  selectCartSkuQuantity,
  selectCartBundleQuantity,
  selectIsBundleCanAddMore,
} from "../../../redux/cart";
import { selectMechanicId } from "../../../redux/auth";

import { useCart } from "../../../hooks/useCart";

import { logSkuOutOfStockQuantity } from "../../../services/sku";

import { APP_PATHS } from "../../../constants/paths";
import { SKUS_QUERY_STRING } from "../../../constants/skus";

import QuantityControls from "../../QuantityControls/QuantityControls";
import PrimaryButton from "../../UI/PrimaryButton/PrimaryButton";
import LoadingOverlay from "../../UI/Loading/LoadingOverlay/LoadingOverlay";
import Price from "../../Price/Price";
import AddedToCart from "../../UI/AddedToCart/AddedToCart";
import Breadcrumb from "../../UI/Breadcrumb/Breadcrumb";
import { AddToCartBtn } from "../../sku";
import { OutOfStockModal } from "../../UI/Modals";

import classes from "./SkuBundleInfo.module.css";

const SkuBundleInfo = (props) => {
  const {
    id,
    brandName,
    categoryName,
    categoryId,
    subcategoryName,
    subcategoryId,
    name,
    price,
    isSkuIncludedInBundle,
    isCartRuleApplied,
    availableQuantity,
    images,
    bundleSkus,
    isInfoForSku,
  } = props;

  const [quantityCounter, setQuantityCounter] = useState(1);
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);

  const userId = useSelector(selectMechanicId);
  const isItemInCart = useSelector((state) =>
    isInfoForSku
      ? selectIsSkuInCart(state, id)
      : selectIsBundleInCart(state, id)
  );
  const cartItemQuantity = useSelector((state) =>
    isInfoForSku
      ? selectCartSkuQuantity(state, id)
      : selectCartBundleQuantity(state, id)
  );
  const isBundleCanAddMore = useSelector((state) =>
    selectIsBundleCanAddMore(state, id)
  );

  const {
    skuQuantityIncreasedHandler,
    skuQuantityDecreasedHandler,
    bundleQuantityIncreasedHandler,
    bundleQuantityDecreasedHandler,
    isLoading,
  } = useCart();

  function closeOutOfStockModalHandler() {
    setIsOutOfStockModalOpen(false);
  }

  const addToCartHandler = async () => {
    // add sku is handled through the add-to-cart-btn component
    await bundleQuantityIncreasedHandler(id, 0, quantityCounter, bundleSkus);
  };

  const itemQuantityIncreasedHandler = async () => {
    if (isItemInCart) {
      if (isInfoForSku) {
        if (cartItemQuantity >= availableQuantity) {
          try {
            logSkuOutOfStockQuantity(id, cartItemQuantity + 1);
            amplitude.track("order item quantity out of stock", {
              userId,
              skuId: id,
              quantity: cartItemQuantity + 1,
            });
          } catch (err) {
            console.error(err);
          }
          setIsOutOfStockModalOpen(true);
          return;
        }
        await skuQuantityIncreasedHandler(id, price, cartItemQuantity, 1);
      } else {
        await bundleQuantityIncreasedHandler(
          id,
          cartItemQuantity,
          1,
          bundleSkus
        );
      }
    } else {
      setQuantityCounter((prevQuantityCounter) => prevQuantityCounter + 1);
    }
  };

  const itemQuantityDecreasedHandler = async () => {
    if (isItemInCart) {
      if (isInfoForSku) {
        await skuQuantityDecreasedHandler(id, price, cartItemQuantity, 1);
      } else {
        await bundleQuantityDecreasedHandler(
          id,
          cartItemQuantity,
          1,
          bundleSkus
        );
      }
    } else {
      setQuantityCounter((prevQuantityCounter) => prevQuantityCounter - 1);
    }
  };

  const isPlusDisabled = useMemo(() => {
    let isDisabled = false;

    if (!isInfoForSku) {
      if (isItemInCart) {
        isDisabled = !isBundleCanAddMore;
      } else {
        isDisabled = quantityCounter >= availableQuantity;
      }
    }

    return isDisabled;
  }, [
    availableQuantity,
    isBundleCanAddMore,
    isInfoForSku,
    isItemInCart,
    quantityCounter,
  ]);

  const getSkuBreadcrumbPaths = () => {
    return [
      {
        url: `${APP_PATHS.SKUS.url}?${SKUS_QUERY_STRING.CATEGORY_NAME}=${categoryName}&${SKUS_QUERY_STRING.CATEGORY_ID}=${categoryId}`,
        label: categoryName,
      },
      {
        url: `${APP_PATHS.SKUS.url}?${SKUS_QUERY_STRING.CATEGORY_NAME}=${categoryName}&${SKUS_QUERY_STRING.CATEGORY_ID}=${categoryId}&${SKUS_QUERY_STRING.SUB_CATEGORY_ID}=${subcategoryId}`,
        label: subcategoryName,
      },
    ];
  };

  return (
    <div className={classes["sku-bundle-info"]}>
      <p className={classes["sku-bundle-info__title"]}>
        {isInfoForSku ? brandName : "مجموعة عرض"}
      </p>
      {isInfoForSku && (
        <Breadcrumb
          paths={getSkuBreadcrumbPaths()}
          className={classes["sku-bundle-info__breadcrumb"]}
        />
      )}
      <p className={classes["sku-bundle-info__name"]}>{name}</p>
      <div className={classes["sku-bundle-info__price-wrapper"]}>
        <Price
          value={price}
          suffix="جنيه"
          className={classes["sku-bundle-info__price"]}
          valueClassName={classes["sku-bundle-info__price-value"]}
          suffixClassName={classes["sku-bundle-info__price-currency"]}
        />
        {isSkuIncludedInBundle && (
          <Link
            className={classes["sku-bundle-info__bundle-tag"]}
            to={`${APP_PATHS.BUNDLES.url}?skuId=${id}`}
          >
            شوف العروض المتوفرة
          </Link>
        )}
      </div>

      <div className={classes["sku-bundle-info__controls-wrapper"]}>
        <p className={classes["sku-bundle-info__quantity"]}>الكمية</p>
        <div className={classes["sku-bundle-info__controls"]}>
          <QuantityControls
            quantity={isItemInCart ? cartItemQuantity : quantityCounter}
            onPlus={itemQuantityIncreasedHandler}
            isPlusDisabled={isPlusDisabled}
            onMinus={itemQuantityDecreasedHandler}
          />
          {isItemInCart ? (
            <AddedToCart isSkuAdded={isInfoForSku} />
          ) : isInfoForSku ? (
            <AddToCartBtn
              className={classes["sku-bundle-info__primary-btn"]}
              id={id}
              price={price}
              imageUrl={images[0]}
              name={name}
              availableQty={availableQuantity}
              qtyToAdd={quantityCounter}
              showNotifyMe={!isCartRuleApplied}
            />
          ) : (
            <PrimaryButton
              className={classes["sku-bundle-info__primary-btn"]}
              onClick={addToCartHandler}
            >
              أضف الى العربة
            </PrimaryButton>
          )}
        </div>
      </div>
      {isLoading && <LoadingOverlay />}
      {isOutOfStockModalOpen && (
        <OutOfStockModal
          isOpen={isOutOfStockModalOpen}
          onClose={closeOutOfStockModalHandler}
          skuId={id}
          imageUrl={images[0]}
          name={name}
          price={price}
          message="بنعتذر عن عدم توفر الكمية"
          showNotifyMe={!isCartRuleApplied}
        />
      )}
    </div>
  );
};

export default SkuBundleInfo;

SkuBundleInfo.propTypes = {
  id: PropTypes.number.isRequired,
  categoryName: PropTypes.string,
  categoryId: PropTypes.number,
  subcategoryName: PropTypes.string,
  subcategoryId: PropTypes.number,
  brandName: PropTypes.string,
  name: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  isSkuIncludedInBundle: PropTypes.bool,
  isCartRuleApplied: PropTypes.bool,
  availableQuantity: PropTypes.number,
  images: PropTypes.arrayOf(PropTypes.string),
  bundleSkus: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, quantity: PropTypes.number })
  ),
  isInfoForSku: PropTypes.bool.isRequired,
};
